/**
 * Byte av hyresgäst — reglerna för den nya perioden.
 *
 * Den nya hyresgästen tar över en uthyrning som styrelsen redan har godkänt.
 * Perioden får därför inte sträcka sig utanför den godkända: start tidigast i
 * dag och tidigast den godkända starten, slut senast det godkända slutet.
 *
 * Samma regler körs i webbläsaren (så att ägaren får beskedet direkt) och i
 * callablen changeTenant (så att de gäller oavsett vad klienten skickar).
 * Speglas i functions/src/utils/tenantChange.ts — functions är ett eget paket.
 */
import { toDateOnly } from './dates';

export type ApprovedPeriod = { start: string | null; end: string | null };

type ApplicationDates = {
  fromDate?: unknown;
  toDate?: unknown;
  contractStartDate?: unknown;
  contractEndDate?: unknown;
};

/**
 * Den godkända uthyrningsperioden, ÅÅÅÅ-MM-DD. fromDate/toDate är det
 * styrelsen godkände; avtalsdatumen är reserv för äldre ansökningar som
 * saknar dem.
 */
export function approvedRentalPeriod(app: ApplicationDates | null | undefined): ApprovedPeriod {
  if (!app) return { start: null, end: null };
  return {
    start: toDateOnly(app.fromDate) ?? toDateOnly(app.contractStartDate),
    end: toDateOnly(app.toDate) ?? toDateOnly(app.contractEndDate),
  };
}

/** Dagens datum i svensk tid, ÅÅÅÅ-MM-DD. */
export function todayDateOnly(): string {
  return toDateOnly(new Date()) as string;
}

export type PeriodCheck =
  | { ok: true; start: string; end: string }
  | { ok: false; message: string };

/**
 * Kontrollera den nya perioden mot den godkända. Datumen jämförs som
 * ÅÅÅÅ-MM-DD-strängar — det är entydigt och oberoende av tidszon.
 */
export function validateTenantChangePeriod(input: {
  approved: ApprovedPeriod;
  newStart: unknown;
  newEnd: unknown;
  today?: string;
}): PeriodCheck {
  const start = toDateOnly(input.newStart);
  const end = toDateOnly(input.newEnd);
  if (!start || !end) {
    return { ok: false, message: 'Start- och slutdatum måste vara giltiga datum.' };
  }
  if (end <= start) {
    return { ok: false, message: 'Slutdatum måste vara efter startdatum.' };
  }
  const today = input.today ?? todayDateOnly();
  if (start < today) {
    return { ok: false, message: 'Startdatum kan inte vara i det förflutna.' };
  }
  const { start: approvedStart, end: approvedEnd } = input.approved;
  if (!approvedEnd) {
    return {
      ok: false,
      message: 'Den godkända uthyrningen saknar slutdatum. Hör av dig till Hyria-support.',
    };
  }
  if ((approvedStart && start < approvedStart) || end > approvedEnd) {
    const range = approvedStart ? `${approvedStart} – ${approvedEnd}` : `till och med ${approvedEnd}`;
    return {
      ok: false,
      message: `Nya perioden måste ligga inom den godkända uthyrningen (${range}).`,
    };
  }
  return { ok: true, start, end };
}

/**
 * Avtalsperioden (det som står i hyresavtalet) måste ligga inom den nya
 * uthyrningsperioden. Saknas avtalsdatum används periodens.
 */
export function validateContractWithinPeriod(input: {
  periodStart: string;
  periodEnd: string;
  contractStart?: unknown;
  contractEnd?: unknown;
}): PeriodCheck {
  const start = input.contractStart ? toDateOnly(input.contractStart) : input.periodStart;
  const end = input.contractEnd ? toDateOnly(input.contractEnd) : input.periodEnd;
  if (!start || !end) {
    return { ok: false, message: 'Avtalets start- och slutdatum måste vara giltiga datum.' };
  }
  if (end < start || start < input.periodStart || end > input.periodEnd) {
    return {
      ok: false,
      message: `Avtalsperioden måste ligga inom den nya uthyrningsperioden (${input.periodStart} – ${input.periodEnd}).`,
    };
  }
  return { ok: true, start, end };
}
